<template>
  <div class="vmas">
    <APageHead
      title="바우처 · 기관 Master 관리"
      sub="기관 Master 는 승인 강사 중에서 관리자가 지정합니다. Master 는 강사 화면에서 기관 바우처를 관리합니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="reloadAll">새로고침</ABtn>
        <ABtn kind="primary" icon="person_add" @click="openAssign()">Master 지정</ABtn>
      </template>
    </APageHead>

    <!-- Master 없는 활성 기관 (GET /voucher/admin/masters/without-master) -->
    <div v-if="withoutMaster.length" class="vmas__banner">
      <i class="material-icons-round">warning</i>
      <div>
        <strong>Master 가 없는 활성 기관 {{ withoutMaster.length }}곳</strong> — 바우처 지정 · 관리를 할 사람이 없습니다.
        <div class="vmas__banner-orgs">
          <button
            v-for="o in withoutMaster"
            :key="o.organization_idx ?? o.idx"
            type="button"
            class="vmas__banner-org"
            @click="openAssign(o)"
          >{{ o.name }} <i class="material-icons-round">person_add</i></button>
        </div>
      </div>
    </div>

    <ACard overflow="visible">
      <div class="vmas__filters">
        <div class="p-field">
          <label class="p-field__label">기관</label>
          <select v-model="filters.organization_idx" class="p-input" @change="search">
            <option value="">전체 기관</option>
            <option v-for="o in orgOptions" :key="o.idx" :value="o.idx">{{ o.name }}</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">검색어</label>
          <input v-model.trim="filters.q" class="p-input" type="text" placeholder="이름 · 이메일" @keyup.enter="search" />
        </div>
        <div class="p-field">
          <label class="p-field__label">상태</label>
          <select v-model="filters.status" class="p-input" @change="search">
            <option value="active">지정 중</option>
            <option value="released">해제됨</option>
            <option value="all">전체</option>
          </select>
        </div>
        <div class="vmas__filter-actions">
          <ABtn kind="primary" icon="search" @click="search">조회</ABtn>
        </div>
      </div>
    </ACard>

    <ACard>
      <template #action>
        <APill kind="info">{{ total.toLocaleString() }}명</APill>
      </template>
      <div v-if="loading" class="vmas__loading"><div class="p-spinner p-spinner--lg"></div></div>
      <LoadError v-else-if="loadError" :message="loadError" @retry="load" />
      <AEmpty v-else-if="!items.length" title="Master 가 없습니다" />
      <template v-else>
        <ATable :columns="columns" :rows="items" row-key="users_idx">
          <template #cell:user="{ row }">
            <div class="vmas__stack">
              <span class="vmas__strong">{{ row.name || '-' }}</span>
              <span class="vmas__sub">{{ row.email || '' }}</span>
            </div>
          </template>
          <template #cell:orgs="{ row }">
            <div class="vmas__orgs">
              <span v-for="o in row.organizations || []" :key="o.organization_idx" class="vmas__org" :class="{ 'is-released': isReleased(o) }">
                <router-link :to="{ name: 'VoucherOrganizationDetail', params: { organizationIdx: o.organization_idx }, query: { tab: 'members' } }">
                  {{ o.name || o.organization_name || `#${o.organization_idx}` }}
                </router-link>
                <span v-if="isReleased(o)" class="vmas__sub">해제</span>
              </span>
            </div>
          </template>
          <template #cell:warn="{ row }">
            <div class="vmas__badges">
              <!-- 강사 권한이 회수됐는데 Master 로 남아 있으면 기관 기능이 막힙니다. 해제하거나 권한을 다시 승인해야 합니다. -->
              <APill v-if="row.instructor_revoked" kind="err" title="강사 권한이 회수됐습니다. Master 를 해제하거나 강사 권한을 다시 승인해 주세요.">강사 권한 없음</APill>
              <!-- 탈퇴가 막혀 있으므로 탈퇴 시도는 해제 요청으로 보고 연락합니다(최근 30일). -->
              <APill
                v-if="isRecentWithdrawalAttempt(row.withdrawal_attempted_at)"
                kind="warn"
                :title="`탈퇴 시도 ${fmtKst(row.withdrawal_attempted_at)} — Master 해제 요청으로 보고 연락해 주세요.`"
              >탈퇴 시도</APill>
            </div>
          </template>
          <template #cell:actions="{ row }">
            <div class="vmas__actions">
              <ABtn size="sm" kind="ghost" icon="info" icon-only title="상세 · 이력" @click="openDetail(row)" />
              <ABtn v-if="activeOrgs(row).length" size="sm" kind="danger" icon="person_remove" @click="openRelease(row)">해제</ABtn>
            </div>
          </template>
        </ATable>
        <APagination :current-page="page" :total="total" :page-size="size" @change="onPage" />
      </template>
    </ACard>

    <!-- 지정 -->
    <AModal v-model="assign.open" size="md" title="Master 지정" :close-on-backdrop="!assign.saving">
      <div class="vmas__form">
        <div class="p-field">
          <label class="p-field__label">기관 <span class="p-field__req">*</span></label>
          <select v-model="assign.organization_idx" class="p-input">
            <option value="">기관 선택</option>
            <option v-for="o in orgOptions" :key="o.idx" :value="o.idx">{{ o.name }}</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">대상 이메일 <span class="p-field__req">*</span></label>
          <div class="vmas__search">
            <input v-model.trim="assign.email" class="p-input" type="email" placeholder="이메일 전체 (정확히 일치)" @keyup.enter="searchTarget" />
            <ABtn kind="ghost" icon="search" :loading="assign.searching" @click="searchTarget">찾기</ABtn>
          </div>
        </div>
        <div v-if="assign.target" class="vmas__target" :class="{ 'is-bad': assign.target.is_instructor === false }">
          <div class="vmas__stack">
            <span class="vmas__strong">{{ assign.target.name || '-' }}</span>
            <span class="vmas__sub">{{ assign.target.email }}</span>
          </div>
          <APill v-if="assign.target.is_instructor === false" kind="err">강사 아님</APill>
          <APill v-else kind="ok">승인 강사</APill>
        </div>
        <p v-if="assign.target && assign.target.is_instructor === false" class="vmas__err">강사 권한 승인을 먼저 해 주세요.</p>
        <p v-if="assign.target && (assign.target.memberships || []).length" class="vmas__hint">
          현재 소속: {{ assign.target.memberships.map((m) => `${m.organization_name} (${memberRoleLabel(m.role)})`).join(', ') }}
        </p>
        <p v-if="assign.notFound" class="vmas__err">{{ assign.notFound }}</p>
        <div class="p-field">
          <label class="p-field__label">메모</label>
          <input v-model.trim="assign.memo" class="p-input" type="text" maxlength="300" />
          <span class="p-field__hint">지정하면 본인에게 이메일이 갑니다.</span>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" :disabled="assign.saving" @click="assign.open = false">취소</ABtn>
        <ABtn kind="primary" :loading="assign.saving" :disabled="!canAssign" @click="submitAssign">지정</ABtn>
      </template>
    </AModal>

    <!-- 해제 -->
    <AModal v-model="release.open" size="md" title="Master 해제" :close-on-backdrop="!release.saving">
      <div v-if="release.row" class="vmas__form">
        <p class="vmas__line"><b>{{ release.row.name }}</b> <span class="vmas__sub">{{ release.row.email }}</span></p>
        <div class="p-field">
          <label class="p-field__label">기관 <span class="p-field__req">*</span></label>
          <select v-model="release.organization_idx" class="p-input">
            <option v-for="o in activeOrgs(release.row)" :key="o.organization_idx" :value="String(o.organization_idx)">
              {{ o.name || o.organization_name }}
            </option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">사유 <span class="p-field__req">*</span></label>
          <textarea v-model.trim="release.reason" class="p-input" rows="3" maxlength="500"></textarea>
        </div>
        <label class="p-check">
          <input v-model="release.keep_as_instructor" type="checkbox" />
          기관 강사로 남기기
        </label>
        <p class="vmas__hint">
          {{ release.keep_as_instructor
            ? 'Master 권한만 빼고 이 기관의 강사로 남깁니다. 진행 중인 클래스 지정은 그대로 유지됩니다.'
            : '기관 멤버에서 뺍니다. 이 사람 클래스에 진행 중 지정이 있으면 목록을 보여 드리고 확인을 받습니다.' }}
        </p>
      </div>
      <template #foot>
        <ABtn kind="ghost" :disabled="release.saving" @click="release.open = false">취소</ABtn>
        <ABtn kind="danger-fill" :loading="release.saving" :disabled="!release.reason || !release.organization_idx" @click="submitRelease">해제</ABtn>
      </template>
    </AModal>

    <!-- 상세 · 이력 -->
    <AModal v-model="detail.open" size="lg" :title="detail.data?.name || 'Master 상세'" :sub="detail.data?.email || ''">
      <div v-if="detail.loading" class="vmas__loading"><div class="p-spinner"></div></div>
      <div v-else-if="detail.data" class="vmas__form">
        <h6 class="vmas__h">기관별 역할</h6>
        <ATable :columns="detailOrgCols" :rows="detail.data.organizations || []" row-key="organization_idx">
          <template #cell:org="{ row }">{{ row.name || row.organization_name || `#${row.organization_idx}` }}</template>
          <template #cell:role="{ row }">
            <APill :kind="memberRoleKind(row.role)">{{ memberRoleLabel(row.role) }}</APill>
            <APill v-if="isReleased(row)" kind="default">해제</APill>
          </template>
          <template #cell:period="{ row }">
            <div class="vmas__stack">
              <span class="vmas__mono">지정 {{ fmtKst(row.assigned_at || row.joined_at) }}</span>
              <span v-if="row.released_at" class="vmas__mono vmas__sub">해제 {{ fmtKst(row.released_at) }}</span>
            </div>
          </template>
          <template #cell:reason="{ row }">
            <span class="vmas__sub">{{ row.release_reason || row.reason || row.memo || '—' }}</span>
          </template>
        </ATable>

        <h6 class="vmas__h">이력</h6>
        <AEmpty v-if="!(detail.data.history || []).length" size="sm" title="이력이 없습니다" />
        <ul v-else class="vmas__timeline">
          <li v-for="(h, i) in detail.data.history" :key="i">
            <span class="vmas__mono vmas__sub">{{ fmtKst(h.at) }}</span>
            <APill size="sm" :kind="historyEventKind(h.event)" :title="h.event">{{ historyEventLabel(h.event) }}</APill>
            <span>{{ h.organization_name || '' }}</span>
            <span class="vmas__sub">{{ [historyActorLabel(h.actor), h.reason].filter(Boolean).join(' · ') }}</span>
          </li>
        </ul>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="detail.open = false">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import {
  fmtKst, pageOf, bodyOf, errorCode, errorDetail, errorMessage, loadErrorText, memberRoleLabel, memberRoleKind,
  historyEventLabel, historyEventKind, historyActorLabel,
} from "@/utils/voucher";
import LoadError from "@/views/voucher/components/LoadError.vue";
import { confirmCloseVouchers } from "@/views/voucher/components/activeVouchers";

const WITHDRAWAL_BADGE_DAYS = 30;

const blankAssign = () => ({ open: false, organization_idx: "", email: "", target: null, notFound: "", searching: false, memo: "", saving: false });

export default {
  name: "VoucherMasters",
  components: { APageHead, ACard, ABtn, APill, ATable, AModal, AEmpty, APagination, LoadError },
  data() {
    return {
      withoutMaster: [],
      orgOptions: [],
      filters: { organization_idx: "", q: "", status: "active" },
      items: [],
      total: 0,
      page: 1,
      size: 20,
      loading: false,
      loadError: "",
      assign: blankAssign(),
      release: { open: false, row: null, organization_idx: "", reason: "", keep_as_instructor: true, saving: false },
      detail: { open: false, loading: false, data: null },
      columns: [
        { key: "user", label: "Master", width: 240 },
        { key: "orgs", label: "기관", wrap: true },
        { key: "warn", label: "확인할 것", width: 200 },
        { key: "actions", label: "", width: 130, align: "right" },
      ],
      detailOrgCols: [
        { key: "org", label: "기관", width: 200 },
        { key: "role", label: "역할", width: 140 },
        { key: "period", label: "지정 · 해제 (KST)", width: 200 },
        { key: "reason", label: "사유", wrap: true },
      ],
    };
  },
  computed: {
    canAssign() {
      return !!this.assign.organization_idx && !!this.assign.target && this.assign.target.is_instructor !== false;
    },
  },
  created() {
    if (this.$route.query.organization_idx) this.filters.organization_idx = String(this.$route.query.organization_idx);
    // 사용자 상세의 탈퇴 차단 안내에서 넘어오면 그 사람으로 좁혀 둡니다.
    if (this.$route.query.q) this.filters.q = String(this.$route.query.q);
    this.loadOrgOptions();
    this.reloadAll();
  },
  methods: {
    fmtKst,
    memberRoleLabel,
    memberRoleKind,
    historyEventLabel,
    historyEventKind,
    historyActorLabel,
    isReleased(o) {
      return !!o.released_at || o.status === "released" || o.status === "RELEASED";
    },
    activeOrgs(row) {
      return (row?.organizations || []).filter((o) => !this.isReleased(o));
    },
    isRecentWithdrawalAttempt(at) {
      if (!at) return false;
      const t = new Date(at).getTime();
      return Number.isFinite(t) && Date.now() - t <= WITHDRAWAL_BADGE_DAYS * 86400000;
    },
    reloadAll() {
      this.loadWithoutMaster();
      this.load();
    },
    async loadOrgOptions() {
      try {
        const res = await this.$axios({ method: "get", url: "/voucher/admin/organizations", params: { status: "Y", page: 1, size: 500 }, loadingBar: false });
        this.orgOptions = pageOf(res).items.map((o) => ({ idx: String(o.idx ?? o.organization_idx), name: o.name }));
      } catch (e) {
        console.error("기관 목록 조회 실패:", e);
      }
    },
    async loadWithoutMaster() {
      try {
        const res = await this.$axios({ method: "get", url: "/voucher/admin/masters/without-master", loadingBar: false });
        this.withoutMaster = pageOf(res).items;
      } catch (e) {
        console.error("Master 없는 기관 조회 실패:", e);
        this.withoutMaster = [];
      }
    },
    search() {
      this.page = 1;
      this.load();
    },
    onPage(p) {
      this.page = p;
      this.load();
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const params = { page: this.page, size: this.size, status: this.filters.status };
        if (this.filters.organization_idx) params.organization_idx = this.filters.organization_idx;
        if (this.filters.q) params.q = this.filters.q;
        const res = await this.$axios({ method: "get", url: "/voucher/admin/masters", params, loadingBar: true });
        const p = pageOf(res, this.page);
        this.items = p.items;
        this.total = p.total;
      } catch (e) {
        console.error("Master 목록 조회 실패:", e);
        this.loadError = loadErrorText(e);
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    // ── 지정 ──
    openAssign(org) {
      this.assign = { ...blankAssign(), open: true, organization_idx: org ? String(org.organization_idx ?? org.idx) : this.filters.organization_idx };
    },
    async searchTarget() {
      const A = this.assign;
      if (!A.email) return;
      A.searching = true;
      A.target = null;
      A.notFound = "";
      try {
        const res = await this.$axios({ method: "get", url: "/voucher/admin/masters/search", params: { email: A.email }, loadingBar: false });
        // 단건 객체 {users_idx, name, email, is_instructor, memberships[]} 입니다(가이드 §3-2).
        A.target = bodyOf(res) || null;
      } catch (e) {
        const code = errorCode(e);
        if (code === "USER_NOT_FOUND" || e?.response?.status === 404) A.notFound = "이 이메일의 계정이 없습니다.";
        else if (code === "ORG_MEMBER_EMAIL_AMBIGUOUS") A.notFound = "같은 이메일의 활성 계정이 둘 이상입니다. 사용자 관리에서 계정을 먼저 정리해 주세요.";
        else swal.fire("검색하지 못했습니다", errorMessage(e, "대상을 찾지 못했습니다."), "error");
      } finally {
        A.searching = false;
      }
    },
    async submitAssign() {
      const A = this.assign;
      A.saving = true;
      try {
        const data = { organization_idx: Number(A.organization_idx), users_idx: A.target.users_idx };
        if (A.memo) data.memo = A.memo;
        await this.$axios({ method: "post", url: "/voucher/admin/masters", data, loadingBar: true });
        A.open = false;
        swal.fire("지정했습니다", "본인에게 안내 이메일이 발송됩니다.", "success");
        this.reloadAll();
      } catch (e) {
        const code = errorCode(e);
        if (code === "ORG_MEMBER_NOT_INSTRUCTOR") swal.fire("승인 강사가 아닙니다", "강사 권한 승인을 먼저 해 주세요.", "warning");
        else if (code === "ORG_MEMBER_DUPLICATE") swal.fire("이미 이 기관의 Master 입니다", "", "info");
        else swal.fire("지정하지 못했습니다", errorMessage(e, "Master 를 지정하지 못했습니다."), "error");
      } finally {
        A.saving = false;
      }
    },

    // ── 해제 ──
    openRelease(row) {
      const orgs = this.activeOrgs(row);
      const preferred = this.filters.organization_idx && orgs.some((o) => String(o.organization_idx) === this.filters.organization_idx)
        ? this.filters.organization_idx
        : String(orgs[0]?.organization_idx ?? "");
      this.release = { open: true, row, organization_idx: preferred, reason: "", keep_as_instructor: true, saving: false };
    },
    async submitRelease() {
      const R = this.release;
      const call = (closeVouchers) =>
        this.$axios({
          method: "post",
          url: `/voucher/admin/masters/${R.row.users_idx}/release`,
          data: {
            organization_idx: Number(R.organization_idx),
            reason: R.reason,
            keep_as_instructor: R.keep_as_instructor,
            close_vouchers: closeVouchers,
          },
          loadingBar: true,
        });
      R.saving = true;
      try {
        try {
          await call(false);
        } catch (e) {
          // "기관 강사로 남기기" 를 끄면 멤버에서 빠지므로, 진행 중 지정이 있으면 서버가 409 로 목록을 줍니다.
          if (errorCode(e) !== "ORG_MEMBER_HAS_ACTIVE_VOUCHERS") throw e;
          if (!(await confirmCloseVouchers(errorDetail(e)))) return;
          await call(true);
        }
        R.open = false;
        swal.fire("해제했습니다", "", "success");
        this.reloadAll();
      } catch (e) {
        swal.fire("해제하지 못했습니다", errorMessage(e, "Master 를 해제하지 못했습니다."), "error");
      } finally {
        R.saving = false;
      }
    },

    // ── 상세 ──
    async openDetail(row) {
      this.detail = { open: true, loading: true, data: null };
      try {
        const res = await this.$axios({ method: "get", url: `/voucher/admin/masters/${row.users_idx}`, loadingBar: false });
        this.detail.data = { name: row.name, email: row.email, ...(bodyOf(res) || {}) };
      } catch (e) {
        swal.fire("오류", errorMessage(e, "상세를 불러오지 못했습니다."), "error");
        this.detail.open = false;
      } finally {
        this.detail.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.vmas {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__banner {
    display: flex;
    gap: 10px;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    background: $p-err-soft;
    color: $p-err-ink;
    border: 1px solid $p-err;
    font-size: 13px;

    > i { font-size: 18px; }
  }

  &__banner-orgs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }

  &__banner-org {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 7px;
    border: 1px solid $p-err;
    background: $p-panel;
    color: $p-err-ink;
    font-size: 12px;
    cursor: pointer;

    i { font-size: 14px; }
  }

  &__filters {
    display: grid;
    grid-template-columns: repeat(3, minmax(160px, 260px)) auto;
    gap: 12px;
    align-items: end;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__filter-actions { display: flex; gap: 6px; }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 40px;
  }

  &__stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__strong { font-weight: 700; color: $p-ink; }
  &__sub { font-size: 11px; color: $p-ink-soft; }
  &__mono { font-family: $p-font-mono; font-variant-numeric: tabular-nums; font-size: 11px; }

  &__orgs,
  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__org {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 7px;
    background: $p-panel-alt;
    border: 1px solid $p-line;
    font-size: 12px;

    a { color: $p-info-ink; font-weight: 700; }

    &.is-released {
      opacity: 0.6;

      a { text-decoration: line-through; }
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__search { display: flex; gap: 6px; }

  &__target {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: $p-radius;
    border: 1px solid $p-line;
    background: $p-panel-alt;

    &.is-bad { border-color: $p-err; background: $p-err-soft; }
  }

  &__err {
    margin: 0;
    font-size: 12px;
    color: $p-err-ink;
    font-weight: 700;
  }

  &__hint {
    margin: 0;
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__line { margin: 0; font-size: 13px; }

  &__h {
    margin: 4px 0 0;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: $p-ink-mid;
  }

  &__timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    li {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      font-size: 13px;
      padding-bottom: 6px;
      border-bottom: 1px solid $p-line-soft;
    }
  }
}
</style>
